// PDF and PNG downloads of the printable page. Ported unchanged from the original pages:
//   1. The brand fonts are fetched and inlined (shared/api embeddedFontCss), then html-to-image draws the page at 4× resolution.
//   2. If the fonts can't be fetched (blocked network, Adobe Fonts down), PDF falls back to the browser's print dialog, where
//      the fonts are already loaded on screen; PNG says it can't be made.
// The image and PDF libraries are large, so they load only when someone exports.
import { embeddedFontCss } from '@/shared/api';
import { downloadUrl } from '@/shared/lib';

// A message about the export: 'ok' done, 'busy' working, '' a problem.
export type SetStatus = (msg: string, cls?: '' | 'ok' | 'busy') => void;

export interface ExportTarget {
  page: () => HTMLElement | null;       // the printable page (the first element inside the preview's page box)
  landscape: boolean;
  pdf: { format: 'letter' | [number, number]; size: [number, number] };
  printSize: string;                     // CSS @page size for the print fallback
  fileName: string;
}

function printFallback(t: ExportTarget){
  const style = document.createElement('style');
  style.id = '_pStyle';
  style.textContent = `
    @page{size:${t.printSize};margin:0}
    @media print{
      body>*{display:none!important}
      #_pTarget{display:block!important}
    }`;
  document.head.appendChild(style);

  const el = t.page();
  const wrap = document.createElement('div');
  wrap.id = '_pTarget';
  wrap.className = 'label-page'; // the labels' own fenced-off text settings (app.css), never the app's look
  wrap.style.cssText = 'display:none;position:fixed;inset:0;background:#fff;z-index:99999';
  if(el) wrap.appendChild(el.cloneNode(true));
  document.body.appendChild(wrap);

  setTimeout(()=>{
    window.print();
    setTimeout(()=>{
      style.remove();
      wrap.remove();
    }, 2000);
  }, 120);
}

async function capture(t: ExportTarget, setStatus: SetStatus){
  const el = t.page();
  if(!el) return null;

  await document.fonts.ready;
  await new Promise(r => setTimeout(r, 150));

  setStatus('Embedding fonts…','busy');
  const fontEmbedCSS = await embeddedFontCss();

  /* No font CSS: return null so callers fall back to the print dialog. */
  if(!fontEmbedCSS){
    return null;   // trigger print fallback
  }

  const { toPng } = await import('html-to-image');
  return toPng(el, {
    pixelRatio      : 4,
    backgroundColor : '#ffffff',
    cacheBust       : true,
    fontEmbedCSS    : fontEmbedCSS,
    fetchRequestInit: {mode:'cors'},
    imagePlaceholder: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
  });
}

export async function exportPDF(t: ExportTarget, setStatus: SetStatus){
  setStatus('Preparing…','busy');
  try{
    const dataUrl = await capture(t, setStatus);
    if(!dataUrl){
      /* font fetch failed — use browser print (fonts guaranteed) */
      setStatus('Opening print dialog…','busy');
      printFallback(t);
      setStatus('Use "Save as PDF" in the print dialog ✓','ok');
      return;
    }
    const { jsPDF } = await import('jspdf');
    const [pw,ph] = t.pdf.size;
    const pdf = new jsPDF({orientation:t.landscape?'landscape':'portrait',unit:'mm',format:t.pdf.format});
    pdf.addImage(dataUrl,'PNG',0,0,pw,ph);
    pdf.save(`${t.fileName}.pdf`);
    setStatus('PDF saved ✓','ok');
  }catch(e){
    setStatus('Opening print dialog…','busy');
    printFallback(t);
    setStatus('Use "Save as PDF" in the print dialog ✓','ok');
    console.error(e);
  }
}

export async function exportPNG(t: ExportTarget, setStatus: SetStatus){
  setStatus('Preparing…','busy');
  try{
    const dataUrl = await capture(t, setStatus);
    if(!dataUrl){ setStatus('PNG unavailable — try PDF export',''); return; }
    downloadUrl(dataUrl, `${t.fileName}.png`);
    setStatus('PNG saved ✓','ok');
  }catch(e){ setStatus('Export failed',''); console.error(e); }
}
